import { useCallback, useRef } from 'react';

import { useCallbackRef } from '../../core/hooks/useCallbackRef.js';
import type { ValidationState } from './types.js';

type ValidationStateSignature = {
  errorKeys: string[];
  status: ValidationState['status'];
  trigger: ValidationState['trigger'];
};

/**
 * Emits validation changes only when the public validation summary changes.
 *
 * @internal
 */
export function useValidationStateEmitter(
  onValidityChange: ((state: ValidationState) => void) | undefined,
): (state: ValidationState) => void {
  const lastSignatureRef = useRef<ValidationStateSignature>(undefined);
  const lastBlurSignatureRef = useRef<{
    errorKeys: string[];
    status: ValidationState['status'];
  }>(undefined);
  const onValidityChangeCallback = useCallbackRef(onValidityChange);

  return useCallback(
    (state: ValidationState) => {
      const lastSignature = lastSignatureRef.current;

      const nextSignature = {
        errorKeys: state.errors
          .map((e) => e.validityStateKey.toString())
          .sort((a, b) => a.localeCompare(b)),
        status: state.status,
        trigger: state.trigger,
      };

      if (lastSignature === undefined && nextSignature.trigger === 'change') {
        return;
      }

      // Suppress duplicate emissions: same status AND same error keys.
      const sameSignature =
        lastSignature !== undefined &&
        lastSignature.status === nextSignature.status &&
        lastSignature.errorKeys.length === nextSignature.errorKeys.length &&
        lastSignature.errorKeys.every(
          (key, i) => key === nextSignature.errorKeys[i],
        );

      // A transition TO 'blur' fires when the validity (errors + status) has
      // changed since the last blur — consumers use trigger:'blur' to mark a
      // field as touched, but re-firing with the same validity on every blur
      // is redundant noise. The reverse (blur → change with same state) is
      // suppressed because the linter re-confirming the same result after a
      // blur is redundant.
      const lastBlurSignature = lastBlurSignatureRef.current;
      const blurValidityUnchanged =
        lastBlurSignature !== undefined &&
        lastBlurSignature.status === nextSignature.status &&
        lastBlurSignature.errorKeys.length === nextSignature.errorKeys.length &&
        lastBlurSignature.errorKeys.every(
          (key, i) => key === nextSignature.errorKeys[i],
        );
      const triggerChangedToBlur =
        lastSignature !== undefined &&
        lastSignature.trigger !== 'blur' &&
        nextSignature.trigger === 'blur' &&
        !blurValidityUnchanged;

      if (
        !triggerChangedToBlur &&
        sameSignature &&
        nextSignature.trigger !== 'manual'
      ) {
        return;
      }

      lastSignatureRef.current = nextSignature;
      if (nextSignature.trigger === 'blur') {
        lastBlurSignatureRef.current = {
          errorKeys: nextSignature.errorKeys,
          status: nextSignature.status,
        };
      }
      onValidityChangeCallback(state);
    },
    [onValidityChangeCallback],
  );
}
